<template>
    <div>
        <div class="col-lg-12">
            <!-- Toast Notification -->
            <div v-if="copySuccess" class="toast-container">
                <div class="toast-message">
                    Link copied successfully!
                </div>
            </div>

            <!-- Conditional Banner Section -->
            <!-- Only show banners if the event is not live -->
            <div v-if="event.event_status !== 'LIVE-EVENT'">
                <!-- Upload Documents Banner - only show if documents are missing -->
                <div v-if="showBanner && !allDocumentsSubmitted" class="notification notice">
                    <p class="notice-text">
                        Identity Documents and Bank Details are required to start receiving payments.
                    </p>
                    <ul class="requirements-list">
                        <li @click="navigateTo('bank-details')">
                <span class="requirement-status">
                    <i v-if="corporateDocuments.bank_details_submitted" class="fas fa-check check-icon"></i>
                    <i v-else class="fas fa-times cross-icon"></i>
                </span>
                            <span class="requirement-label">Bank Details</span>
                        </li>
                        <li @click="navigateTo('business-documents')">
                <span class="requirement-status">
                    <i v-if="corporateDocuments.business_documents_submitted" class="fas fa-check check-icon"></i>
                    <i v-else class="fas fa-times cross-icon"></i>
                </span>
                            <span class="requirement-label">Business Documents</span>
                        </li>
                        <li @click="navigateTo('authorized-individuals')">
                <span class="requirement-status">
                    <i v-if="corporateDocuments.authorized_individuals_submitted" class="fas fa-check check-icon"></i>
                    <i v-else class="fas fa-times cross-icon"></i>
                </span>
                            <span class="requirement-label">Authorized Individuals</span>
                        </li>
                    </ul>
                    <!-- Payment Methods -->
                    <div class="payment-section">
                        <p class="payment-title">Accept all major credit cards</p>
                        <div class="payment-logos">
                            <img src="/images/payment/mastercard.png" alt="Mastercard" />
                            <img src="/images/payment/visa.png" alt="Visa" />
                        </div>
                    </div>
                    <div class="payment-section">
                        <p class="payment-title">Accept Ecocash Payments</p>
                        <div class="payment-logos">
                            <img src="/img/EcoCash.svg" alt="EcoCash" class="ecoCash-logo" />
                        </div>
                    </div>
                    <!-- Upload Button -->
                    <div class="button-container mt-3">
                        <router-link
                            to="/business/settings/banking-details"
                            class="btn btn-sm btn-success text-white shadow-none"
                        >
                            <i class="fas fa-upload mr-2"></i> Upload Documents and Bank Details
                        </router-link>
                    </div>
                </div>

                <!-- Approval Banner - only show if awaiting approval or approved -->
                <div v-else-if="event.event_status === 'AWAITING-APPROVAL' || event.event_status === 'APPROVED'" class="notification notice">
                    <p class="notice-text" v-if="event.event_status === 'AWAITING-APPROVAL'">
                        You have successfully submitted all required documents. Your event is now awaiting approval.
                    </p>
                    <p class="notice-text" v-else-if="event.event_status === 'APPROVED'">
                        Your event has been approved! You can now go live.
                    </p>
                </div>
                </div>
            <!-- Cards Section: Summary Metrics -->
            <div class="row gy-4 mt-4">
                <div class="col-12 col-sm-12 col-lg-3 mb-2">
                    <div class="card shadow-sm rounded text-center bg-white px-3 py-4">
                        <small class="service-text-small">Tickets Sold</small>
                        <hr />
                        <p class="mb-0 text-3 service-text"><b>{{ summary.issued_tickets }}</b></p>
                    </div>
                </div>
                <div class="col-12 col-sm-12 col-lg-3 mb-2">
                    <div class="card shadow-sm rounded text-center bg-white px-3 py-4">
                        <small class="service-text-small">Revenue</small>
                        <hr />
                        <p class="mb-0 text-3 service-text"><b>${{ summary.revenue }}</b></p>
                    </div>
                </div>
                <div class="col-12 col-sm-12 col-lg-3 mb-2">
                    <div class="card shadow-sm rounded text-center bg-white px-3 py-4">
                        <small class="service-text-small">Days to Go</small>
                        <hr />
                        <p class="mb-0 text-3 service-text"><b>{{ summary.days_to_go }} days</b></p>
                    </div>
                </div>
                <div class="col-12 col-sm-12 col-lg-3 mb-2">
                    <div class="card shadow-sm rounded text-center bg-white px-3 py-4">
                        <small class="service-text-small">Checked In</small>
                        <hr />
                        <p class="mb-0 text-3 service-text"><b>{{ computedCheckedIn }}</b></p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Event Link / Preview Card -->
        <div class="card mt-4">
            <header class="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                <h4 class="text-6">Event Page Link</h4>
                <a :href="encodedEventLink" target="_blank" class="btn btn-sm btn-primary shadow-none">
                    Preview Event Page
                </a>
            </header>
            <div class="px-3 py-3">
                <input
                    style="width: 100%;"
                    class="form-control form-field--sharable event-link mb-3"
                    :value="encodedEventLink"
                    readonly
                    @click="$event.target.select()"
                />
            </div>
            <!-- Card Footer with icons -->
            <div class="card-footer bg-white d-flex align-items-center border-0 justify-content-start">
                <div class="icon-wrapper me-4" @click="downloadQR" title="Download QR Code">
                    <i class="fas fa-qrcode fa-lg icon"></i>
                    <span class="icon-label">Download QR</span>
                </div>
                <div class="icon-wrapper" @click="copyLink" title="Copy Link">
                    <i class="fas fa-copy fa-lg icon"></i>
                    <span class="icon-label">Copy Link</span>
                </div>
            </div>
        </div>

        <!-- Ticket Sales Overview Section -->
        <div class="card rounded mt-4 border-0">
            <div class="card-header d-flex justify-content-between align-items-center bg-white">
                <h4 class="text-6">Ticket Sales</h4>
                <router-link :to="`/business/edit-events/${$route.params.id}`" class="btn btn-sm btn-link custom-underline font-weight-bold">
                    Manage Ticket Types
                </router-link>
            </div>
            <div class="card-body">
                <div v-for="ticket in summary.ticket_types" :key="ticket.id" class="mb-3">
                    <div class="d-flex justify-content-between align-items-start">
            <span class="fw-bold">
              <i class="fas fa-ticket-alt"></i>
              {{ ticket.name }} <b>×{{ ticket.total_tickets }}</b>
            </span>
                        <div class="ticket_tally w-50 text-end">
                            <div class="d-flex justify-content-between">
                                <span>{{ ticket.issued_tickets }} tickets sold</span>
                                <span>{{ ticket.remaining_tickets }} tickets remaining</span>
                            </div>
                            <div class="progress_container">
                                <div class="progress bg-light" style="height: 10px; width: 100%;">
                                    <div class="progress-bar bg-primary"
                                         :style="{ width: (ticket.total_tickets > 0 ? ((ticket.issued_tickets / ticket.total_tickets) * 100) : 0) + '%' }">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr />
                </div>
                <router-link class="btn btn-sm btn-primary shadow-none mt-3"
                             :class="{ active: $route.path.includes('/report') }"
                             :to="`/business/events/${$route.params.id || ''}/report`">
                    Load Ticket Sales Summary
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import axios from "axios";
const PUBLIC_SITE_URL = "https://magetsi.co.zw";

export default {
    name: "Summary",
    props: ["permissions"],
    data() {
        return {
            event: {},
            summary: {
                issued_tickets: 0,
                total_tickets: 0,
                remaining_tickets: 0,
                revenue: 0,
                days_to_go: 0,
                checked_in: 0,
                ticket_types: [],
            },
            corporateDocuments: {
                bank_details_submitted: false,
                business_documents_submitted: false,
                authorized_individuals_submitted: false,
            },
            copySuccess: false,
        };
    },
    computed: {
        encodedEventLink() {
            const slug = this.event.slug || this.slugify(this.event.name || '');
            if (!slug) return "";
            return `${PUBLIC_SITE_URL}/tickets/${slug}`;
        },
        computedCheckedIn() {
            return this.summary.checked_in || 0;
        },
        // Checks if all required documents are submitted.
        allDocumentsSubmitted() {
            const docs = this.corporateDocuments || {};
            return (
                docs.bank_details_submitted &&
                docs.business_documents_submitted &&
                docs.authorized_individuals_submitted
            );
        },
        showBanner() {
            return !this.allDocumentsSubmitted;
        },
    },
    methods: {
        encodeEventId(id) {
            const firstPass = btoa(id.toString());
            return btoa(firstPass)
                .replace(/=+$/,"")
                .replace(/\+/g,"-")
                .replace(/\//g,"_");
        },

        slugify(text) {
            return String(text)
                    .normalize("NFKD")
                    .replace(/[\u0300-\u036f]/g, "")       // strip diacritics
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")           // non-alphanum -> dash
                    .replace(/^-+|-+$/g, "")               // trim dashes
                || "event";
        },
        fetchSummary() {
            const eventId = this.$route.params.id;
            axios
                .get(`/business/event-onboarding/events/${eventId}/api/summary`)
                .then((response) => {
                    if (response.data.body?.summary && response.data.body?.event) {
                        this.summary = response.data.body.summary;
                        this.event = response.data.body.event;
                        // Emit global events for updating parent layout if needed.
                        this.$root.$emit("update-event-title", this.event.name);
                        this.$root.$emit("update-event-status", this.event.event_status);
                        this.$root.$emit("update-current-event", this.event);
                        if (response.data.body.corporate_documents) {
                            this.corporateDocuments = response.data.body.corporate_documents;
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error fetching summary data:", error);
                });
        },
        navigateTo(formType) {
            const routes = {
                "bank-details": `/business/events/${this.$route.params.id}/bank-details`,
                "business-documents": `/business/events/${this.$route.params.id}/business-documents`,
                "authorized-individuals": `/business/events/${this.$route.params.id}/authorized-individuals`,
            };
            if (routes[formType]) {
                this.$router.push(routes[formType]);
            }
        },
        downloadQR() {
            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(this.encodedEventLink)}`;
            const a = document.createElement("a");
            a.href = qrUrl;
            a.download = `event-${this.event.id}-qr.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        copyLink() {
            navigator.clipboard.writeText(this.encodedEventLink)
                .then(() => {
                    this.$toast.success("Link Copied Successfully!", { timeout: 3000 });
                })
                .catch(err => console.error("Failed to copy: ", err));
        },
    },
    mounted() {
        this.fetchSummary();
        this.pollingInterval = setInterval(this.fetchSummary, 30000);
    },
    beforeDestroy() {
        clearInterval(this.pollingInterval);
    },
};
</script>

<style scoped>
.notification {
    background: #252C65;
    padding: 20px;
    border-left: 5px solid #252C65;
    border-radius: 5px;
    margin-top: 20px;
}
.btn {
    display: inline-block;
    margin-top: 10px;
    padding: 10px 15px;
    color: white;
    text-decoration: none;
    border-radius: 5px;
}
.notification.notice {
    background-color: #d4edda;
    border-color: #029f2f;
}
.payment-title {
    margin-bottom: 3px;
    font-weight: 600;
}
.ecoCash-logo {
    width: 80px;
    height: auto;
}
.notice-text {
    margin: 0 0 15px;
    font-size: 16px;
}
.requirements-list {
    list-style: none;
    padding: 0;
    margin: 0 0 15px;
}
.requirements-list li {
    display: flex;
    align-items: center;
    font-size: 16px;
    margin-bottom: 5px;
}
.requirement-status {
    margin-right: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: #fff;
}
.check-icon {
    color: #28a745;
    font-size: 16px;
}
.cross-icon {
    color: #dc3545;
    font-size: 16px;
}
.button-container .btn-primary {
    background-color: #155724;
    border-color: #155724;
}
.button-container .btn-primary:hover {
    background-color: #136d21;
    border-color: #136d21;
}
.custom-underline {
    color: #28a745;
    text-decoration: underline;
    text-decoration-color: #28a745;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
}
.progress_container {
    width: 100%;
    height: 12px;
    background-color: #e0e0e0;
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
    margin-top: 6px;
}
.progress {
    transition: width 0.4s ease-in-out;
}
.icon-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: color 0.2s ease;
}
.icon-wrapper:hover {
    color: #252C65;
}
.icon-label {
    font-size: 0.75rem;
    margin-top: 0.25rem;
    color: #6c757d;
}
.icon-wrapper:not(:last-child) {
    margin-right: 2rem;
}
.icon {
    font-size: 1.25rem;
    margin-right: 0.5rem;
}
.toast-container {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1050;
}
.toast-message {
    background-color: #28a745;
    color: #fff;
    padding: 15px 20px;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    font-size: 14px;
    transition: opacity 0.3s ease;
}
</style>
